import { useState, useCallback, useEffect, useRef } from 'react';

// ────────────────────────────────────────────────────────────
// Constantes / helpers
// ────────────────────────────────────────────────────────────

let _compteurId = 0;
const genId = () => `id_${++_compteurId}_${Date.now()}`;

const CLE_STOCKAGE = 'i2c_etude';
const INTERVALLE_AUTOSAVE_MS = 5 * 60 * 1000; // 5 minutes

// ────────────────────────────────────────────────────────────
// État initial
// ────────────────────────────────────────────────────────────

const etatInitial = {
  // ── Données communes : réutilisées par tous les documents ──
  commun: {
    infosGenerales: {
      // Nom court saisi une seule fois, combiné à l'année/mois pour former
      // l'identifiant de l'étude (voir documents/utilitairesReference.js).
      // Ne PAS taper "NumDoc_CE_" ni l'année/le mois ici.
      reference: '',
      nomEtude: '',
      nomAuteur: '',
      // Année et mois : remplis automatiquement depuis la date de
      // l'ordinateur à la création de l'étude (voir plus bas). Restent
      // modifiables si besoin (ex: étude commencée fin décembre).
      anneeCreation: String(new Date().getFullYear()),
      moisCreation: String(new Date().getMonth() + 1).padStart(2, '0'),
      logo: null,
    },
    client: {
      genre: '',
      prenom: '',
      nom: '',
      fonction: '',
      mail: '',
      nomEntreprise: '',
      adresse: '',
      siret: '',
    },
    i2c: {
      typePresidence: 'president', // 'president' ou 'co-president'
      infoPresident1: { prenom: '', nom: '', genre: '', mail: '' },
      infoPresident2: { prenom: '', nom: '', genre: '', mail: '' },
      contacts: [{ id: genId(), prenom: '', nom: '', genre: '', description: '', role: '', mail: '', tel: '', auteur: false }],
    },
    phases: [{ id: genId(), designation: '', nombreJeh: '', description_phase: '', prixUnitaire: '' }],
    livrables: [{ id: genId(), nom: '', garantie: '12' }],
    fraisDossier: '100',
    conditionsPaiement: { pctSignature: '55', pctLivrable: '45' },
    // Compteur unique pour toute l'étude : incrémenté à CHAQUE génération de
    // document (peu importe le type), y compris en cas de régénération.
    prochainNumDoc: 1,
    // Phases/livrables cochés au fur et à mesure de l'avancement de l'étude.
    // Partagés entre PVRI et PVRF : les deux documents piochent dans la même
    // liste, remplie progressivement (le PVRI capture un sous-ensemble tôt,
    // le PVRF capture ce qui est coché au moment de la clôture).
    phasesRealiseesIds: [],
    livrablesRemisIds: [],
    nombreIntervenants: '',
  },

  // ── Données propres à chaque document ───────────────────────
  documents: {
    conventionEtude: {
      projet: { contexte: '', problematique: '', definitionBesoin: '', critereValidation: '' },
      contraintes: [],
      exigences: [],
      pointsControleActifs: false,
      pointsControle: [],
      moyensOutils: '',
      derniereGeneration: null,
      numDoc: null, // NumDoc attribué à la dernière génération de ce document
    },
    devis: {
      dateValidite: '',
      derniereGeneration: null,
      numDoc: null,
    },
    pvri: {
      derniereGeneration: null,
      numDoc: null,
    },
    pvrf: {
      derniereGeneration: null,
      numDoc: null,
    },
    cdc: {
      derniereGeneration: null,
      numDoc: null,
    },
  },
};

// ────────────────────────────────────────────────────────────
// Persistance locale + migration depuis l'ancienne structure
// ────────────────────────────────────────────────────────────

function chargerEtude() {
  try {
    const sauvegarde = localStorage.getItem(CLE_STOCKAGE);
    if (!sauvegarde) return null;
    return migrerEtude(JSON.parse(sauvegarde));
  } catch (e) {
    console.error("Erreur de lecture de l'étude sauvegardée :", e);
    return null;
  }
}

/**
 * Migre une étude sauvegardée vers la structure actuelle.
 * Deux cas :
 *  1. Ancienne structure à plat (avant l'introduction de commun/documents)
 *     → reconstruction complète.
 *  2. Structure déjà à jour, mais sauvegardée AVANT l'ajout d'un champ
 *     (ex: prochainNumDoc, numDoc, moisCreation) → on complète juste les
 *     champs manquants avec leur valeur par défaut, sans toucher au reste.
 * Ça évite de casser les étuces déjà sauvegardées à chaque évolution du
 * schéma de données.
 */
function migrerEtude(donnees) {
  if (!donnees || typeof donnees !== 'object') return donnees;

  if (donnees.commun) {
    // Récupère d'éventuelles anciennes sélections séparées PVRI/PVRF
    // (avant qu'elles ne soient fusionnées en une liste commune) pour ne pas
    // perdre les cases déjà cochées.
    const ancienPvri = donnees.documents?.pvri ?? {};
    const ancienPvrf = donnees.documents?.pvrf ?? {};
    const fusionnerIds = (...listes) => [...new Set(listes.flat().filter(Boolean))];

    return {
      ...donnees,
      commun: {
        ...donnees.commun,
        infosGenerales: { ...etatInitial.commun.infosGenerales, ...donnees.commun.infosGenerales },
        prochainNumDoc: donnees.commun.prochainNumDoc ?? 1,
        phasesRealiseesIds:
          donnees.commun.phasesRealiseesIds ??
          fusionnerIds(ancienPvri.phasesConcerneesIds, ancienPvrf.phasesConcerneesIds),
        livrablesRemisIds:
          donnees.commun.livrablesRemisIds ??
          fusionnerIds(ancienPvri.livrablesRemisIds, ancienPvrf.livrablesRemisIds),
        // Ancien emplacement : documents.devis.nombreIntervenants
        nombreIntervenants:
          donnees.commun.nombreIntervenants ?? donnees.documents?.devis?.nombreIntervenants ?? '',
      },
      documents: Object.fromEntries(
        Object.entries(etatInitial.documents).map(([id, valeurParDefaut]) => [
          id,
          { ...valeurParDefaut, ...(donnees.documents?.[id] ?? {}) },
        ])
      ),
    };
  }

  const i2cAncien = donnees.i2c || {};
  const i2cMigre = {
    typePresidence: i2cAncien.typePresidence ?? i2cAncien.signataire ?? 'president',
    infoPresident1: i2cAncien.infoPresident1 ?? i2cAncien.infoSignataire ?? { prenom: '', nom: '', genre: '', mail: '' },
    infoPresident2: i2cAncien.infoPresident2 ?? i2cAncien.infoSignataire2 ?? { prenom: '', nom: '', genre: '', mail: '' },
    contacts: i2cAncien.contacts ?? [],
  };

  return {
    commun: {
      infosGenerales: { ...etatInitial.commun.infosGenerales, ...donnees.infosGenerales },
      client: donnees.client ?? etatInitial.commun.client,
      i2c: i2cMigre,
      phases: donnees.phases ?? etatInitial.commun.phases,
      livrables: donnees.livrables ?? etatInitial.commun.livrables,
      fraisDossier: donnees.fraisDossier ?? etatInitial.commun.fraisDossier,
      conditionsPaiement: donnees.conditionsPaiement ?? etatInitial.commun.conditionsPaiement,
      prochainNumDoc: 1,
      phasesRealiseesIds: [],
      livrablesRemisIds: [],
      nombreIntervenants: '',
    },
    documents: {
      conventionEtude: {
        projet: donnees.projet ?? etatInitial.documents.conventionEtude.projet,
        contraintes: donnees.contraintes ?? [],
        exigences: donnees.exigences ?? [],
        pointsControleActifs: donnees.pointsControleActifs ?? false,
        pointsControle: donnees.pointsControle ?? [],
        moyensOutils: donnees.moyensOutils ?? '',
        derniereGeneration: null,
        numDoc: null,
      },
      devis: { ...etatInitial.documents.devis },
      pvri: { ...etatInitial.documents.pvri },
      pvrf: { ...etatInitial.documents.pvrf },
    },
  };
}

/**
 * Hook principal — gère l'état de toute l'étude (données communes +
 * données propres à chaque document) et expose des handlers typés.
 */
function useEtude() {
  const [formData, setFormData] = useState(() => chargerEtude() || etatInitial);
  const [derniereSauvegarde, setDerniereSauvegarde] = useState(null);

  // Ref toujours à jour avec le dernier formData, pour que sauvegarderEtude
  // et l'intervalle d'autosave n'aient pas besoin de se recréer à chaque
  // frappe (sinon le setInterval repart de zéro à chaque changement, et
  // l'autosave ne se déclenche plus jamais pendant une saisie active).
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // ── Sauvegarde locale (manuelle ou auto) ──────────────────
  const sauvegarderEtude = useCallback(() => {
    try {
      localStorage.setItem(CLE_STOCKAGE, JSON.stringify(formDataRef.current));
      setDerniereSauvegarde(new Date());
      return true;
    } catch (e) {
      console.error('Erreur de sauvegarde locale :', e);
      return false;
    }
  }, []);

  const reinitialiserEtude = useCallback(() => {
    localStorage.removeItem(CLE_STOCKAGE);
    setFormData(etatInitial);
    setDerniereSauvegarde(null);
  }, []);

  // sauvegarderEtude a maintenant une identité stable : cet effet ne se
  // déclenche qu'une fois, l'intervalle de 5 minutes n'est donc plus jamais
  // interrompu par les changements de formData.
  useEffect(() => {
    const intervalle = setInterval(() => {
      sauvegarderEtude();
    }, INTERVALLE_AUTOSAVE_MS);
    return () => clearInterval(intervalle);
  }, [sauvegarderEtude]);

  // ── Helpers génériques ─────────────────────────────────────
  const mettreAJourCommun = useCallback((section, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, [section]: { ...prev.commun[section], [cle]: valeur } },
    }));
  }, []);

  // Champ simple d'un document (ex: 'devis' + 'dateValidite')
  const changerChampDocument = useCallback((docId, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [docId]: { ...prev.documents[docId], [cle]: valeur },
      },
    }));
  }, []);

  // Ajoute/retire un id d'une liste de sélection d'un document
  const toggleElementDansListe = useCallback((docId, champ, id) => {
    setFormData((prev) => {
      const liste = prev.documents[docId][champ];
      const nouvelleListe = liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id];
      return {
        ...prev,
        documents: {
          ...prev.documents,
          [docId]: { ...prev.documents[docId], [champ]: nouvelleListe },
        },
      };
    });
  }, []);

  // Même chose, mais pour une liste commune (ex: phasesRealiseesIds,
  // livrablesRemisIds — partagées entre PVRI et PVRF).
  const toggleElementDansListeCommun = useCallback((champ, id) => {
    setFormData((prev) => {
      const liste = prev.commun[champ];
      const nouvelleListe = liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id];
      return {
        ...prev,
        commun: { ...prev.commun, [champ]: nouvelleListe },
      };
    });
  }, []);

  // À appeler après une génération réussie. numDocUtilise est le numéro qui
  // vient d'être attribué à ce document (lu depuis formData.commun.prochainNumDoc
  // AVANT l'appel au générateur, voir composants/vues/VueXxx.js) — on le
  // stocke sur le document, et on avance le compteur pour la prochaine fois.
  const marquerDocumentGenere = useCallback((docId, numDocUtilise) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, prochainNumDoc: prev.commun.prochainNumDoc + 1 },
      documents: {
        ...prev.documents,
        [docId]: {
          ...prev.documents[docId],
          derniereGeneration: new Date().toISOString(),
          numDoc: numDocUtilise,
        },
      },
    }));
  }, []);

  // ── Infos générales / Client (communs) ─────────────────────
  const changerInfoGenerale = useCallback(
    (cle, valeur) => mettreAJourCommun('infosGenerales', cle, valeur),
    [mettreAJourCommun]
  );

  const changerClient = useCallback(
    (cle, valeur) => mettreAJourCommun('client', cle, valeur),
    [mettreAJourCommun]
  );

  // ── Projet (spécifique Convention d'Étude) ──────────────────
  const changerProjet = useCallback((cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          projet: { ...prev.documents.conventionEtude.projet, [cle]: valeur },
        },
      },
    }));
  }, []);

  // ── Contraintes (spécifique Convention d'Étude) ─────────────
  const ajouterContrainte = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          contraintes: [...prev.documents.conventionEtude.contraintes, { id: genId(), nom: '', description: '' }],
        },
      },
    }));
  }, []);

  const supprimerContrainte = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          contraintes: prev.documents.conventionEtude.contraintes.filter((c) => c.id !== id),
        },
      },
    }));
  }, []);

  const modifierContrainte = useCallback((id, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          contraintes: prev.documents.conventionEtude.contraintes.map((c) => (c.id === id ? { ...c, [cle]: valeur } : c)),
        },
      },
    }));
  }, []);

  // ── Exigences fonctionnelles (spécifique Convention d'Étude) ──
  const ajouterExigence = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          exigences: [...prev.documents.conventionEtude.exigences, { id: genId(), nom: '', description: '', critere: '' }],
        },
      },
    }));
  }, []);

  const supprimerExigence = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          exigences: prev.documents.conventionEtude.exigences.filter((e) => e.id !== id),
        },
      },
    }));
  }, []);

  const modifierExigence = useCallback((id, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          exigences: prev.documents.conventionEtude.exigences.map((e) => (e.id === id ? { ...e, [cle]: valeur } : e)),
        },
      },
    }));
  }, []);

  // ── Livrables (communs) ─────────────────────────────────────
  const ajouterLivrable = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, livrables: [...prev.commun.livrables, { id: genId(), nom: '', garantie: '12' }] },
    }));
  }, []);

  const supprimerLivrable = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, livrables: prev.commun.livrables.filter((l) => l.id !== id) },
    }));
  }, []);

  const modifierLivrable = useCallback((id, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, livrables: prev.commun.livrables.map((l) => (l.id === id ? { ...l, [cle]: valeur } : l)) },
    }));
  }, []);

  // ── Phases (communes) ───────────────────────────────────────
  const ajouterPhase = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      commun: {
        ...prev.commun,
        phases: [...prev.commun.phases, { id: genId(), designation: '', nombreJeh: '', description_phase: '', prixUnitaire: '' }],
      },
    }));
  }, []);

  const supprimerPhase = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, phases: prev.commun.phases.filter((p) => p.id !== id) },
    }));
  }, []);

  const modifierPhase = useCallback((id, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, phases: prev.commun.phases.map((p) => (p.id === id ? { ...p, [cle]: valeur } : p)) },
    }));
  }, []);

  // ── Points de contrôle (spécifique Convention d'Étude) ──────
  const activerPointsControle = useCallback((valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: { ...prev.documents, conventionEtude: { ...prev.documents.conventionEtude, pointsControleActifs: valeur } },
    }));
  }, []);

  const ajouterPointControle = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          pointsControle: [...prev.documents.conventionEtude.pointsControle, { id: genId(), description: '' }],
        },
      },
    }));
  }, []);

  const supprimerPointControle = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          pointsControle: prev.documents.conventionEtude.pointsControle.filter((p) => p.id !== id),
        },
      },
    }));
  }, []);

  const modifierPointControle = useCallback((id, cle, valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        conventionEtude: {
          ...prev.documents.conventionEtude,
          pointsControle: prev.documents.conventionEtude.pointsControle.map((p) => (p.id === id ? { ...p, [cle]: valeur } : p)),
        },
      },
    }));
  }, []);

  // ── Moyens et outils (spécifique Convention d'Étude) ────────
  const changerMoyensOutils = useCallback((valeur) => {
    setFormData((prev) => ({
      ...prev,
      documents: { ...prev.documents, conventionEtude: { ...prev.documents.conventionEtude, moyensOutils: valeur } },
    }));
  }, []);

  // ── Frais dossier / conditions de paiement / autres champs simples communs ──
  const changerFraisDossier = useCallback((valeur) => {
    setFormData((prev) => ({ ...prev, commun: { ...prev.commun, fraisDossier: valeur } }));
  }, []);

  // Setter générique pour un champ simple (non imbriqué) de commun,
  // ex: changerChampCommun('nombreIntervenants', '4').
  const changerChampCommun = useCallback((cle, valeur) => {
    setFormData((prev) => ({ ...prev, commun: { ...prev.commun, [cle]: valeur } }));
  }, []);

  const changerConditionsPaiement = useCallback((cle, valeur) => {
    setFormData((prev) => {
      const pct = Math.min(100, Math.max(0, parseInt(valeur, 10) || 0));
      const conditionsPaiement =
        cle === 'pctSignature'
          ? { pctSignature: String(pct), pctLivrable: String(100 - pct) }
          : { pctLivrable: String(pct), pctSignature: String(100 - pct) };
      return { ...prev, commun: { ...prev.commun, conditionsPaiement } };
    });
  }, []);

  // ── i2c : président(s) et contacts (communs) ────────────────
  const changerTypePresidence = useCallback((valeur) => {
    setFormData((prev) => ({ ...prev, commun: { ...prev.commun, i2c: { ...prev.commun.i2c, typePresidence: valeur } } }));
  }, []);

  const changerInfoPresident = useCallback((cle, valeur, index = 1) => {
    const champ = index === 2 ? 'infoPresident2' : 'infoPresident1';
    setFormData((prev) => ({
      ...prev,
      commun: {
        ...prev.commun,
        i2c: { ...prev.commun.i2c, [champ]: { ...prev.commun.i2c[champ], [cle]: valeur } },
      },
    }));
  }, []);

  const ajouterContact = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      commun: {
        ...prev.commun,
        i2c: {
          ...prev.commun.i2c,
          contacts: [...prev.commun.i2c.contacts, { id: genId(), prenom: '', nom: '', genre: '', description: '', role: '', mail: '', tel: '', auteur: false }],
        },
      },
    }));
  }, []);

  const supprimerContact = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      commun: { ...prev.commun, i2c: { ...prev.commun.i2c, contacts: prev.commun.i2c.contacts.filter((c) => c.id !== id) } },
    }));
  }, []);

  const modifierContact = useCallback((id, cle, valeur) => {
    setFormData((prev) => {
      const contactsMisAJour =
        cle === 'auteur'
          ? prev.commun.i2c.contacts.map((c) => ({ ...c, auteur: c.id === id ? valeur : false }))
          : prev.commun.i2c.contacts.map((c) => (c.id === id ? { ...c, [cle]: valeur } : c));

      let infosGenerales = prev.commun.infosGenerales;
      const doitRecalculer =
        cle === 'auteur' ||
        ((cle === 'nom' || cle === 'prenom') && contactsMisAJour.find((c) => c.id === id)?.auteur);

      if (doitRecalculer) {
        const auteur = contactsMisAJour.find((c) => c.auteur);
        infosGenerales = {
          ...prev.commun.infosGenerales,
          nomAuteur: auteur ? `${auteur.nom} ${auteur.prenom}`.trim() : '',
        };
      }

      return {
        ...prev,
        commun: { ...prev.commun, infosGenerales, i2c: { ...prev.commun.i2c, contacts: contactsMisAJour } },
      };
    });
  }, []);

  return {
    formData,
    derniereSauvegarde,
    sauvegarderEtude,
    reinitialiserEtude,
    // génériques (pour les documents 3+ qui n'ont besoin que de champs simples)
    changerChampDocument,
    toggleElementDansListe,
    toggleElementDansListeCommun,
    marquerDocumentGenere,
    // communs
    changerInfoGenerale,
    changerClient,
    changerTypePresidence, changerInfoPresident,
    ajouterContact, supprimerContact, modifierContact,
    ajouterLivrable, supprimerLivrable, modifierLivrable,
    ajouterPhase, supprimerPhase, modifierPhase,
    changerFraisDossier,
    changerChampCommun,
    changerConditionsPaiement,
    // spécifiques Convention d'Étude
    changerProjet,
    ajouterContrainte, supprimerContrainte, modifierContrainte,
    ajouterExigence, supprimerExigence, modifierExigence,
    activerPointsControle, ajouterPointControle, supprimerPointControle, modifierPointControle,
    changerMoyensOutils,
  };
}

export default useEtude;